import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Upload, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import type { MockView, MocksApi } from '../api/mocks'
import { useOpenInSimulator } from '../questions/openInSimulator'
import { MocksGrid } from './MocksGrid'
import { MockUpload } from './MockUpload'

export interface MocksProps {
  mocks: MocksApi
  exam: string
}

export function Mocks({ mocks, exam }: MocksProps) {
  const client = useQueryClient()
  const list = useQuery({ queryKey: ['mocks', exam], queryFn: () => mocks.list(exam) })

  const [opened, setOpened] = useState<string | null>(null)

  const refresh = () => client.invalidateQueries({ queryKey: ['mocks', exam] })
  const rows = list.data ?? []
  const mock = rows.find((row) => row.code === opened) ?? null
  const failure = list.error?.message ?? null

  return (
    <>
      <div className="grid items-start gap-6 lg:grid-cols-2">
        <h2 className="font-heading text-h2 font-bold text-heading">Mocks Management</h2>
        <MockUpload key={exam} exam={exam} mocks={mocks} onImported={() => {
          void refresh()
          void client.invalidateQueries({ queryKey: ['questions'] })
        }} />
      </div>
      {failure && (
        <p role="alert" className="mt-4 border-l-2 border-red-700 pl-3 text-red-700">
          {failure}
        </p>
      )}

      <MocksGrid rows={rows} pending={list.isPending} onOpen={setOpened} />

      {mock && <MockDrawer mock={mock} mocks={mocks} onChanged={refresh} onClose={() => setOpened(null)} />}
    </>
  )
}

function MockDrawer({
  mock,
  mocks,
  onChanged,
  onClose,
}: {
  mock: MockView
  mocks: MocksApi
  onChanged: () => Promise<void>
  onClose: () => void
}) {
  const draft = mock.status === 'Draft'
  const picker = useRef<HTMLInputElement>(null)
  const publish = useMutation({ mutationFn: () => mocks.publish(mock.code), onSuccess: onChanged })
  // A year's intro file onto the Mock, or off it, and the Mock whole in the Simulator to check it (app:R85).
  const intro = useMutation({
    mutationFn: async (file: File) => mocks.setIntro(mock.code, await file.text()),
    onSuccess: onChanged,
  })
  const clearIntro = useMutation({ mutationFn: () => mocks.clearIntro(mock.code), onSuccess: onChanged })
  const open = useOpenInSimulator(() => mocks.preview(mock.code))
  const counting = useMutation({
    mutationFn: (towards: boolean) => mocks.counting(mock.code, towards),
    onSuccess: onChanged,
  })
  const discard = useMutation({
    mutationFn: () => mocks.discard(mock.code),
    onSuccess: async () => {
      onClose()
      await onChanged()
    },
  })
  const error =
    publish.error?.message ??
    counting.error?.message ??
    discard.error?.message ??
    intro.error?.message ??
    clearIntro.error?.message ??
    open.error?.message ??
    null

  return (
    <Panel title={mock.code} onClose={onClose} error={error}
      footer={
        draft && (
          <>
            <button type="button" className={quiet} disabled={discard.isPending} onClick={() => discard.mutate()}>
              Discard the Draft
            </button>
            <button type="button" className={loud} disabled={publish.isPending} onClick={() => publish.mutate()}>
              Publish
            </button>
          </>
        )
      }
    >
      <p className="text-body">
        {mock.kind}, {mock.status}
      </p>
      <label className="mt-3 flex items-center gap-2">
        <input
          type="checkbox"
          checked={mock.countsTowardsDailyLimit}
          disabled={!draft || counting.isPending}
          onChange={(event) => counting.mutate(event.target.checked)}
        />
        <span>Spends the Student's Qs</span>
      </label>

      <section className="mt-4">
        <h3 className="font-bold text-heading">Before the Questions</h3>
        {mock.introPages ? (
          <ol className="mt-1 list-inside list-decimal text-body">
            {mock.introPages.map((page, index) => (
              <li key={index}>
                {page.before ? `Before ${page.before}` : 'Before the exam'}
                {page.mode ? `, ${page.mode} only` : ''}
                {page.seconds ? `, ${page.seconds} s to read` : ''}
              </li>
            ))}
          </ol>
        ) : (
          <p className="mt-1 text-muted">Opens on Question 1.</p>
        )}
        <div className="mt-2 flex flex-wrap gap-3">
          <input
            ref={picker}
            type="file"
            accept=".yaml,.yml"
            className="hidden"
            aria-label="Intro pages file"
            onChange={(event) => {
              const file = event.target.files?.[0]
              if (file) intro.mutate(file)
              event.target.value = ''
            }}
          />
          <button type="button" className={quiet} disabled={intro.isPending} onClick={() => picker.current?.click()}>
            <Upload aria-hidden size={16} /> {intro.isPending ? 'Uploading...' : 'Upload intro pages'}
          </button>
          {mock.introPages && (
            <button type="button" className={quiet} disabled={clearIntro.isPending} onClick={() => clearIntro.mutate()}>
              Remove intro pages
            </button>
          )}
          <button type="button" className={quiet} disabled={open.isPending} onClick={() => open.mutate(undefined)}>
            {open.isPending ? 'Opening...' : 'View in Simulator'}
          </button>
        </div>
      </section>

      <table className="mt-4 w-full text-left">
        <thead>
          <tr className="border-b border-ink-100">
            <th className="py-1">Position</th>
            <th className="py-1">Question</th>
          </tr>
        </thead>
        <tbody>
          {mock.entries.map((entry) => (
            <tr key={entry.position} className={entry.voidedAt ? 'text-muted line-through' : ''}>
              <td className="py-1">{entry.position}</td>
              <td className="py-1">{entry.question}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Panel>
  )
}

/** The drawer shell the Sittings screen uses: a panel over a dimmed page, Escape closes it. */
function Panel({
  title,
  onClose,
  error,
  footer,
  children,
}: {
  title: string
  onClose: () => void
  error: string | null
  footer: React.ReactNode
  children: React.ReactNode
}) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <>
      <div className="fixed inset-0 z-40 bg-ink-900/20" onClick={onClose} aria-hidden />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="fixed top-0 right-0 z-50 flex h-full w-full max-w-xl flex-col border-l border-ink-100 bg-card shadow-xl"
      >
        <header className="flex items-center gap-3 border-b border-ink-100 p-4">
          <h2 className="font-heading text-h3 font-bold text-heading">{title}</h2>
          <button type="button" onClick={onClose} aria-label="Close" className="ml-auto rounded p-2 hover:bg-green-050">
            <X aria-hidden size={18} />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto p-4">{children}</div>
        <footer className="border-t border-ink-100 p-4">
          {error && (
            <p role="alert" className="mb-3 text-red-700">
              {error}
            </p>
          )}
          <div className="flex justify-end gap-3">{footer}</div>
        </footer>
      </aside>
    </>
  )
}

const quiet =
  'inline-flex items-center gap-2 rounded-md border border-green-800 px-3 py-1.5 font-bold text-green-800 hover:bg-green-050 disabled:opacity-55'

const loud =
  'inline-flex items-center gap-2 rounded-md bg-green-800 px-4 py-2 font-bold text-white hover:bg-green-900 disabled:opacity-55'
